/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

export enum WsMessage {
  HR_UPLOAD_PUBLISHED = 'HR_UPLOAD_PUBLISHED',
  NEW_NOTIFICATIONS = 'NEW_NOTIFICATIONS',
  NEW_NOTIFICATIONS_BATCH = 'NEW_NOTIFICATIONS_BATCH',
  REGISTER = 'REGISTER',
  UNREGISTER = 'UNREGISTER',
}

export class Constants {
  // default prefix for images hosted on fastly
  static readonly WEB_ASSETS_LINK = 'https://nextpro.com.global.prod.fastly.net/web_assets';
  static readonly DEFAULT_AVATAR_ICON_SVG = `/assets/images/svg/rebrand/athlete-default-white.svg`;
  static readonly DEFAULT_AVATAR_ICON_SQUARE_SVG = `/assets/images/svg/blue-avatar-square.svg`;
  static readonly GS_HOSTNAME = `https://www.googleapis.com/storage/v1/b/nextpro`;
  static readonly GS_ASSET_HOSTNAME = `https://storage.googleapis.com/nextpro`;
  static readonly GS_DOWNLOAD_API_KEY = `AIzaSyAlh_HLYLGEzjBo91XZ5mjGsh-i21XzQhY`;

  static readonly PROFILE_REGEX = /^\/profile\/[^\/]*\/\d+/i;
  static readonly PROFILE_REDIRECT_REGEX = /^\/profile\/\d+/i;
  static readonly EVENT_REGEX = /^\/[a-z\-]+\/events\/[^\/]*\/\d+(\/[\w\-]+)?$/i;
  static readonly EVENT_REDIRECT_REGEX = /^\/events\/\d+/i;
  static readonly LIVE_EVENT_REGEX = /^\/[a-z\-]+\/live\/[^\/]*\/\d+(\/[\w\-]+)?/i;
  static readonly CHANNEL_REGEX = /^\/[a-z\-]+\/channels\/[^\/]*\/\d+(\/[\w\-]+)?$/i;
  static readonly CHANNEL_REDIRECT_REGEX = /^\/channels\/\d+/i;
  static readonly EVENT_VIDEO_PLAY_REGEX = /^\/[a-z\-]+\/events\/[^\/]*\/\d+\/[\w-]+\/[^\/]*\/\d+/i;
  static readonly VIDEO_PLAY_REGEX = /^\/video\/[^\/]*\/\d+/i;
  static readonly VIDEO_REDIRECT_REGEX = /^\/redirect\/to\/video\/id\/\d+/i;
  static readonly CLUB_REGEX = /^\/[a-z\-]+\/clubs\/[^\/]*\/\d+(\/[\w\-]+)?$/i;
  static readonly CLUB_REDIRECT_REGEX = /^\/clubs\/\d+/i;
  static readonly MY_LIBRARY_REGEX = /^\/my-library/i;
  static readonly MY_ACCOUNT_REGEX = /^\/my-account/i;
  static readonly LOGIN_REGEX = /^\/login/i;
  static readonly REGISTER_REGEX = /^\/register/i;

  static readonly HIGHLIGHT_REEL_TYPE = 'highlight-reel';

  static readonly HIGHLIGHT_REEL_TYPES = {
    SELECT: 'SELECT',
    PRO: 'PRO',
  };

  static readonly MAX_REEL_PLAYS = {
    SELECT: 20,
    PRO: 5,
  };

  static readonly MIN_REEL_PLAYS = {
    SELECT: 3,
    PRO: 1,
  };

  static readonly USER_EVENT_TYPES = {
    SELECT: 'select',
    ADD: 'add',
  };

  static readonly HIGHLIGHT_REEL_PRO_PLAY_TYPES = {
    SELECT: 'select',
    ADD: 'add',
  };

  static readonly HIGHLIGHT_REEL_SELECT_PLAY_TYPES = {
    SELECT_CLIP_UPLOAD: 'select_clip_upload',
    SELECT_CLIP: 'select_clip',
    ADD_PLAY: 'add_play',
  };

  static readonly EVENT_TYPE = 'event';

  static readonly ASSET_TYPE = {
    GENERIC: 'Generic',
    FULL_GAME_VIDEO: 'Full Game Video',
    ATHLETE_HIGHLIGHT_VIDEO: 'Athlete Highlight Video',
    ATHLETE_CUSTOM_VIDEO: 'Athlete Custom Video',
    ATHLETE_CLIP_VIDEO: 'Athlete Clip Video',
    ATHLETE_GAME_VIDEO: 'Athlete Game Video',
    EVENT_HIGHLIGHT_VIDEO: 'Event Highlight Video',
    TRAINING_VIDEO: 'Training Video',
    EPISODE_VIDEO: 'Episode Video',
    GAME_RECAP_VIDEO: 'Game Recap Video',
    FEATURED_VIDEO: 'Featured Video',
    CLIPPED_VIDEO: 'Clipped Video',
    CLUB_HIGHLIGHT_VIDEO: 'Club Highlight Video',
    PRO_FEEDBACK: 'Pro Feedback Video',
  };

  static readonly EVENT_TYPES = {
    SHOWCASE: 'Showcase',
    TEAM: 'Team',
  };

  static readonly EVENT_BUTTON_TYPES = {
    BUY: 'EVENT_BUY_BUTTON',
    VIEW: 'EVENT_VIEW_BUTTON',
    PURCHASED: 'EVENT_PURCHASED_BUTTON',
    ADD_TO_LIBRARY: 'EVENT_ADD_LIBRARY_BUTTON',
  };

  static readonly USER_TYPE = {
    ADMIN: 'ADMIN',
    ATHLETE: 'ATHLETE',
    COACH: 'COACH',
    HEAD_COACH: 'HEAD_COACH',
    PARENT: 'PARENT',
    USER: 'USER',
  };

  static readonly USER_TYPE_NAME = {
    ADMIN: 'Admin',
    ATHLETE: 'Athlete',
    COACH: 'Coach',
    HEAD_COACH: 'College Coach',
    PARENT: 'Parent',
    USER: 'User',
  };

  static readonly SECTION_CLASSES = {
    SECTION_CONTENT: 'section--content',
    SECTION_CONTENT_MY_GAMES: 'section--content section--my-games',
    SECTION_CONTENT_ENTITIES: 'section--content section--entities',
    SECTION_LIBRARY_EMPTY_CONTENT: 'section--content section--library-empty',
    SECTION_CONTENT_EMPTY: 'section--content section--content-empty',
    SECTION_EVENTS: 'section--content section--filter events',
    SECTION_EMPTY_EVENTS: 'section--content section--content section--filter events section--content-empty-events',
  };

  static readonly NOTIFICATION_TYPE = {
    FOLLOW_REQUEST: 'FOLLOW_REQUEST', // ex: huck finn (@huckfinn) has requested to follow you.
    ACCEPTED_FOLLOW_REQUEST: 'ACCEPTED_FOLLOW_REQUEST', // ex: huck finn (@huckfinn) accepted your follow request.
    NEW_VIDEO_FROM_CHANNEL: 'NEW_VIDEO_FROM_CHANNEL', // Sports Academy has a new video
    // NEW_VIDEO_FROM_USER: 'NEW_VIDEO_FROM_USER', // ex: huck finn (@huckfinn) has a new video
    SHARED_VIDEO: 'SHARED_VIDEO', // from a user/athlete you are following ex: huck finn (@huckfinn) has shared a video with you
    LIKED_VIDEO: 'LIKED_VIDEO', // from a user/athlete you are following ex: huck finn (@huckfinn) liked a video
    COMMENT_LIKED: 'COMMENT_LIKED', // from a user/athlete you are following ex: huck finn (@huckfinn) liked your comment
    COMMENTED_REPLIED: 'COMMENTED_REPLIED', // from a user/athlete you are following ex: huck finn (@huckfinn) commented on your comment
  };

  static readonly ACTION_TARGET = {
    ASSET: 'ASSET',
    CHANNEL: 'CHANNEL',
    USERACTION: 'USERACTION',
    USER: 'USER',
    EVENT: 'EVENT',
    LIVEEVENT: 'LIVEEVENT',
    GAME: 'GAME',
  };

  static readonly ACTION_TYPE = {
    ACCEPTED_FOLLOW_REQUEST: 'ACCEPTED_FOLLOW_REQUEST',
    NEW_VIDEO_FROM_CHANNEL: 'NEW_VIDEO_FROM_CHANNEL',
    FOLLOWED: 'followed',
    LIKED: 'liked',
    SUBSCRIBED: 'subscribed to',
    BLOCKED: 'blocked',
    REPORTED: 'reported',
    REMINDER: 'reminder',
    SHARED: 'shared',
    DOWNLOADED: 'downloaded',
    DOWNLOAD_RESET: 'download reset',
    PLAYED: 'played',
    COMMENTED: 'commented',
    REPLIED: 'replied',
    FLAGGED: 'flagged',
    WATCHING: 'watching',
  };

  static readonly USER_ASSET_DOWNLOAD_LIMIT = 5;

  static readonly VIDEO_QUALITY = {
    q480_30: '_480p_30fps',
    q720_30: '_720p_30fps',
    q720_60: '_720p_60fps',
  };

  static readonly SPORTS_FILTER_TYPE = {
    TRENDING: 'trending',
    POPULAR: 'popular',
    MOST_VIEWED: 'most_viewed',
    ALL: 'all',
  };

  static readonly LIVE_EVENT_STATUS = {
    ENDED: 'STREAMING_ENDED',
  };

  static readonly PRICING_STATES = {
    ADD_SUBSCRIPTION: 'add subscription',
    REGISTER: 'register',
    UPGRADE: 'upgrade',
  };

  static readonly SEARCH_SECTIONS = {
    LIVE_VIDEOS: 'liveEvents',
    HIGHLIGHTS: 'highlights',
    FULL_GAMES: 'fullGames',
    ATHLETES: 'athletes',
    CHANNELS: 'channels',
    EVENTS: 'events',
    CLUBS: 'clubs',
    CLIPS: 'clips',
  };

  static readonly MAX_SUBSCRIPTIONS = 6;

  static readonly SYSTEM_NOTIFICATION_TYPES = {
    INFO: 'info',
    WARNING: 'warning',
    DANGER: 'danger',
  };

  static readonly PURCHASE_TYPE = {
    ONE_EVENT_GF: 'ONE_EVENT_GF',
    ONE_EVENT_GFHR: 'ONE_EVENT_GFHR',
    ONE_EVENT_GFHRP: 'ONE_EVENT_GFHRP',
    ONE_EVENT_TP: 'ONE_EVENT_TP',
    CHANNEL_GF: 'CHANNEL_GF',
    BREAKDOWN: 'BREAKDOWN',
    BREAKDOWN_ONE_GAME: 'BREAKDOWN_ONE_GAME',
    BREAKDOWN_ONE_GAME_TP: 'BREAKDOWN_ONE_GAME_TP',
    HR: 'HR',
    PRO_HR: 'PRO_HR',
    COMPLETE_GP: 'COMPLETE_GP',
    PERFECT_GP: 'PERFECT_GP',
    EXPEDITED_DELIVERY: 'EXPEDITED_DELIVERY',
    ALL_EVENTS_2018: 'ALL_EVENTS_2018',
    PRO_FEEDBACK: 'PRO_FEEDBACK',
    I_EVENT: 'I_EVENT',
    I_GAME: 'I_GAME',
    I_MULTI_GAME: 'I_MULTI_GAME',
    T_EVENT: 'T_EVENT',
    T_GAME: 'T_GAME',
    T_MULTI_GAME: 'T_MULTI_GAME',
  };

  static readonly PURCHASE_PRICE = {
    HR: 250,
    EXPEDITED_DELIVERY: 99,
  };

  static readonly PLAN_TYPE = {
    FREE: 'Free',
    ONE_EVENT: 'One Event',
    ALL_EVENTS: 'All Events',
  };

  static readonly PLAN_TYPE_ICON_PATH = {
    FREE: 'assets/images/svg/icons/misc/flame-icon.svg',
    ONE_EVENT: 'assets/images/svg/icons/misc/crown-icon.svg',
    ALL_EVENTS: 'assets/images/svg/icons/misc/all-star-icon.svg',
  };

  static readonly BREAKDOWN_TYPE = {
    ATHLETE: 'athlete',
    TEAM: 'team',
  };

  static readonly COUPON_USAGE_LIMIT_TYPE = {
    PER_USER: 'PER_USER',
    PER_TOTAL: 'PER_TOTAL',
    UNLIMITED: 'UNLIMITED',
  };

  static readonly COUPON_APPLICABLE_TO_TYPE = {
    ENTIRE_ORDER: 'ENTIRE_ORDER',
    SPECIFIC_EVENT: 'SPECIFIC_EVENT',
    ALL_EVENTS: 'ALL_EVENTS',
    HIGHLIGHT_REEL: 'HIGHLIGHT_REEL',
    HIGHLIGHT_REEL_PRO: 'HIGHLIGHT_REEL_PRO',
  };
  static readonly COUPON_MINIMUM_REQUIREMENT_TYPE = {
    QUANTITY: 'QUANTITY',
    AMOUNT: 'AMOUNT',
  };
  static readonly COUPON_DISCOUNT_TYPE = {
    PERCENTAGE: '%',
    AMOUNT: '$',
  };

  static readonly LOGIN_MODAL_TRIGGERS = {
    COMMENT: 'comment',
    FOLLLOW_ATHLETE: 'follow-athlete',
    LIKE_VIDEO: 'like-video',
    LIKE_CHANNEL: 'like-channel',
    NAVIGATE: 'navigate',
    SEARCH: 'search',
    SHARE: 'share',
    SUBSCRIBE: 'subscribe',
    DOWNLOAD: 'download',
    CLIP: 'clip',
  };

  static readonly DATE_FORMATS = {
    SHORT: 'short',
    NORMAL: 'normal',
  };

  static readonly GRADUATION_YEARS = {
    NON_SOCCER: [
      '2018',
      '2019',
      '2020',
      '2021',
      '2022',
      '2023',
      '2024',
      '2025',
      '2026',
      '2027',
      '2028',
      '2029',
      '2030',
    ],
    SOCCER: ['10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U', '18U', '19U'],
  };

  static readonly SPORTS = {
    LACROSSE: 'lacrosse',
    BASKETBALL: 'basketball',
    BASEBALL: 'baseball',
    SOCCER: 'soccer',
    SOFTBALL: 'softball',
    VOLLEYBALL: 'volleyball',
    FOOTBALL: 'football',
    // FIELD_HOCKEY: 'field-hockey',
    ICE_HOCKEY: 'ice-hockey',
  };

  static readonly NO_HIGHLIGHTS_SPORTS = [
    Constants.SPORTS.BASEBALL,
    Constants.SPORTS.FOOTBALL,
    Constants.SPORTS.SOFTBALL,
    Constants.SPORTS.BASKETBALL,
    // Constants.SPORTS.FIELD_HOCKEY,
    Constants.SPORTS.ICE_HOCKEY,
  ];

  static readonly HOME_FEATURED_VIDEO_TYPES = {
    HIGHLIGHT: 'isFeaturedHighlight',
    CHAMPIONSHIP_GAME: 'isChampionShip',
    ALL_STAR_GAME: 'isAllStarGame',
  };

  static readonly FREE_CONTENT_TYPES = {
    CHAMPIONSHIP_GAME: 'isChampionShip',
    ALL_STAR_GAME: 'isAllStarGame',
  };

  static readonly MAX_HR_PLAYS = 20;

  static readonly PROCESSING_FEE = 0;
  static readonly PROCESSING_FEE_LIMIT = 399;

  static readonly CONFIRMATION_MODAL_TYPES = {
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
  };

  static readonly ORDER_TYPE = {
    ARCHIVED: 'ARCHIVED',
    CANCELED: 'CANCELED',
  };

  static readonly PURCHASE_TYPE_NAME = {
    ONE_EVENT_GF: 'Game Film',
    ONE_EVENT_GFHR: 'Film + Highlight Reel Select',
    ONE_EVENT_GFHRP: 'Film + Highlight Reel Pro',
    ONE_EVENT_TP: 'Team Package',
    BREAKDOWN: 'Breakdown',
    BREAKDOWN_ONE_GAME: 'One Game Breakdown',
    BREAKDOWN_ONE_GAME_TP: 'One Game Team Breakdown',
    CHANNEL_GF: 'Channel Game Film',
    HR: 'Highlight Reel Select',
    PRO_HR: 'Highlight Reel Pro',
    PERFECT_GP: 'Perfect Game Package',
    COMPLETE_GP: 'Complete Game Package',
    EXPEDITED_DELIVERY: 'Expedited Highlight Reel Delivery',
    PRO_FEEDBACK: 'Pro Feedback',
    I_EVENT: 'Individual Event',
    I_GAME: 'Individual Single Game',
    I_MULTI_GAME: 'Individual Multi Game',
    T_EVENT: 'Team Event',
    T_GAME: 'Team Single Game',
    T_MULTI_GAME: 'Team Multi Game',
  };

  static readonly HR_DAYS_TO_DELIVER = {
    STANDARD: 15,
    PRO: 15,
    VOLLEY_BASKETBALL: 20,
    EXPEDITED: 5,
  };

  static readonly BREAKDOWN_DAYS_TO_DELIVER = 6;

  static readonly LOG_TYPES = {
    ADD_CARD: 'Add Card',
    UPDATE_CARD: 'Update Card',
    ADD_INVITES: 'Add Invites',
    COMPLETE: 'Complete',
    CREATE_BRAINTREE_USER: 'Create Braintree User',
    CREATE_ORDER: 'Create Order',
    CREATE_PURCHASE: 'Create Purchase',
    DEVICE_INFO: 'Device Info',
    CART_PRODUCTS_INFO: 'Cart Products Info',
    PURCHASE_COUNT: 'Purchase Count',
    TOKENIZE_CARD: 'Tokenize Card',
    TRANSACTION: 'Transaction',
    SEND_INVITES: 'Send Invites',
    SEND_ORDER_RELATED: 'Send Products Ready Email',
    SIGNUP: 'Signup',
    UNCAUGHT_ERROR: 'Uncaught Error',
  };

  static readonly SORT_VALUES = [
    // { value: 1, name: 'Closest Match' },
    { value: 2, name: 'Most Recent', icon: 'assets/images/svg/icons/misc/clock.svg' },
    { value: 3, name: 'Title', icon: 'assets/images/svg/icons/misc/az.svg' },
    { value: 4, name: 'Most Views', icon: 'assets/images/svg/icons/misc/eye.svg' },
    // { value: 5, name: 'Favorites' },
  ];
  static readonly SORT_TYPES = {
    CLOSEST_MATCH: 1,
    MOST_RECENT: 2,
    TITLE: 3,
    MOST_VIEWS: 4,
    FAVORITES: 5,
  };

  static readonly USER_NOTIFICATION_TYPES = {
    HR_READY: 'HR_READY',
    GAME_FILM_READY: 'GAME_FILM_READY',
    GAME_FILM_NOT_READY: 'GAME_FILM_NOT_READY',
  };

  static readonly INLINE_NOTIFICATION_TYPES = {
    GAME_FILM_READY: 'GAME_FILM_READY',
    GAME_FILM_NOT_READY: 'GAME_FILM_NOT_READY',
    UNSUBMITTED_REEL: 'UNSUBMITTED_REEL',
    PROGRESS_REEL: 'PROGRESS_REEL',
    BREAKDOWN_GAME_NOT_READY: 'BREAKDOWN_GAME_NOT_READY',
    GAME_WO_BREAKDOWN: 'GAME_WO_BREAKDOWN',
  };

  static readonly TRANSCODER_PRIORITIES = {
    LOW: 0,
    MEDIUM: 5,
    HIGH: 10,
  };

  static readonly COACH_ACTION_TYPE = {
    VIDEO_PLAYING: 'Video Playing',
    PROFILE_VIEW: 'Profile View',
  };

  static readonly USER_NEW_VIDEO_TYPE = {
    GAME: 'Game',
    HIGHLIGHT_REEL: 'Highlight Reel',
    CLIP: 'Clip',
    BREAKDOWN: 'Breakdown',
  };

  static readonly USER_UPLOAD_TYPE = {
    ROSTER: 'Roster',
  };

  static readonly BREAKDOWN_STATUSES = {
    OUTSTANDING_FORM: 'Oustanding form',
    NOT_STARTED: 'Not started',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    PUBLISHED: 'Published',
  };

  static readonly FILM_TYPES = {
    GAME: 'GAME',
    DRILL: 'DRILL',
    PERSONAL_CLIP: 'PERSONAL_CLIP',
    DEFENSIVE_SHOWCASE: 'DEFENSIVE_SHOWCASE',
  };
}
